import {
  Application,
  Assets,
  BlurFilter,
  Container,
  Graphics,
  Sprite,
  Text,
  Texture,
} from 'pixi.js';
import {
  POT_OF_GREED_URL,
  RUNTIME_IMAGE_URLS,
  SYMBOL_ARTWORK,
  CARD_BACK_URL,
  HIGH_FIDELITY_ARTWORK_URLS,
  EGYPTIAN_GOD_URLS,
} from './asset-manifest';
import { chooseRenderResolution, DprObserver } from './dpr-observer';
import { getSymbolCardStyle } from './symbol-card-style';
import type {
  GridPosition,
  PresentedLineWin,
  PresentationMode,
  ReplayPresentationStep,
  SlotGrid,
  SpinPresentationResult,
  SymbolId,
  GodPresentationEvent,
} from './types';
import { gridPositionKey } from './types';
import { NOOP_PRESENTATION_SINK, type PresentationEventSink } from '../presentation/events';
import { createPotOfGreedTimeline } from '../presentation/feature-presentation';
import { getVisibleLineRoute, PAYLINE_COLORS, createWinShowcasePlan } from '../presentation/win-showcase';
import { createSpinTimeline, type SpinTimeline } from '../presentation/spin-timeline';
import { createObeliskVisualPlan, type ObeliskVisualPlan } from '../presentation/god-effect-plan';
import { getGodCardCopy, getGodCardTiming } from '../presentation/god-card-presentation';
import {
  TEMPLE_SLOT_LAYOUT,
  fitContain,
  getBoardBounds,
  getCellCenter,
  getCellRect,
  getReelHeight,
} from '../presentation/reel-layout';

const LAYOUT = TEMPLE_SLOT_LAYOUT;
// Match the renderer's maximum density so letters and card captions do not
// become the softest part of the symbol when moving to a high-DPR monitor.
const TEXT_TEXTURE_RESOLUTION = 3;
const SCATTER_ANTICIPATION_COLOR = 0xff5b7d;
const PRESENTATION_FILLER: readonly SymbolId[] = [
  'JACK', 'QUEEN', 'ACE', 'TW', 'KING', 'JINZO', 'DM', 'ACE', 'BEWD', 'QUEEN', 'KING', 'JACK',
];

export interface SlotRendererOptions {
  readonly mode?: PresentationMode;
  readonly eventSink?: PresentationEventSink;
  readonly reducedMotion?: boolean;
  readonly resolutionCap?: number;
}

export interface PresentSpinOptions {
  readonly turbo: boolean;
  readonly reducedMotion?: boolean;
  readonly signal?: AbortSignal;
}

export interface SlotRendererPort {
  initialize(host: HTMLElement, initialGrid: SlotGrid): Promise<void>;
  setMode(mode: PresentationMode): void;
  setGrid(grid: SlotGrid, sticky?: readonly GridPosition[]): void;
  presentSpin(result: SpinPresentationResult, options: PresentSpinOptions): Promise<void>;
  presentReplayStep(step: ReplayPresentationStep, reducedMotion?: boolean): Promise<void>;
  cancel(): void;
  destroy(): void;
}

interface TimedRendererEvent {
  readonly atMs: number;
  readonly type: 'anticipation' | 'reveal-start' | 'stop';
  readonly reel: number;
  readonly scatterStage: 0 | 1 | 2 | 3;
  readonly hasScatter: boolean;
}

interface PerformanceStrip {
  readonly root: Container;
  readonly distance: number;
  readonly stopAtMs: number;
}

function now(): number {
  return globalThis.performance?.now() ?? Date.now();
}

function wait(milliseconds: number, signal?: AbortSignal): Promise<void> {
  return new Promise((resolve, reject) => {
    if (signal?.aborted === true) {
      reject(new DOMException('Präsentation abgebrochen.', 'AbortError'));
      return;
    }
    const handle = globalThis.setTimeout(resolve, Math.max(0, milliseconds));
    signal?.addEventListener('abort', () => {
      globalThis.clearTimeout(handle);
      reject(new DOMException('Präsentation abgebrochen.', 'AbortError'));
    }, { once: true });
  });
}

function isAbortError(error: unknown): boolean {
  return error instanceof DOMException && error.name === 'AbortError';
}

function easeOutCubic(value: number): number {
  return 1 - (1 - value) ** 3;
}

function easeInOutCubic(value: number): number {
  return value < 0.5
    ? 4 * value ** 3
    : 1 - ((-2 * value + 2) ** 3) / 2;
}

function textStyle(size: number, fill: number, weight: '600' | '800' = '800') {
  return {
    fontFamily: 'Cinzel, Georgia, serif',
    fontSize: size,
    fontWeight: weight,
    fill,
    align: 'center' as const,
    dropShadow: { color: 0x02040a, alpha: 0.9, blur: 4, distance: 2 },
  };
}

export class TempleSlotRenderer implements SlotRendererPort {
  private readonly app = new Application();
  private readonly boardLayer = new Container();
  private readonly reelsLayer = new Container();
  private readonly potLayer = new Container();
  private readonly cardsLayer = new Container();
  private readonly anticipationLayer = new Container();
  private readonly winLayer = new Container();
  private readonly effectsLayer = new Container();
  private readonly reelRoots: Container[] = [];
  private readonly reelContents: Container[] = [];
  private readonly reelGlows: Graphics[] = [];
  private readonly textures = new Map<string, Texture>();
  private readonly eventSink: PresentationEventSink;
  private readonly defaultReducedMotion: boolean;
  private readonly resolutionCap: number;
  private initialized = false;
  private destroyed = false;
  private mode: PresentationMode;
  private currentGrid: SlotGrid | null = null;
  private stickyPositions: readonly GridPosition[] = [];
  private controller: AbortController | null = null;
  private densityObserver: DprObserver | null = null;
  private performanceStrips: PerformanceStrip[] = [];
  private idleWinTimer: ReturnType<typeof globalThis.setInterval> | null = null;
  private readonly coveredCellKeys = new Set<string>();
  private potOverlay: Container | null = null;
  private potOverlayPosition: GridPosition | null = null;
  private lingeringObelisk: Readonly<{
    plan: ObeliskVisualPlan;
    grid: SlotGrid;
    sticky: readonly GridPosition[];
  }> | null = null;

  constructor(options: SlotRendererOptions = {}) {
    this.mode = options.mode ?? 'cards';
    this.eventSink = options.eventSink ?? NOOP_PRESENTATION_SINK;
    this.defaultReducedMotion = options.reducedMotion
      ?? globalThis.matchMedia?.('(prefers-reduced-motion: reduce)').matches
      ?? false;
    this.resolutionCap = options.resolutionCap ?? 3;
  }

  async initialize(host: HTMLElement, initialGrid: SlotGrid): Promise<void> {
    if (this.initialized || this.destroyed) throw new Error('Renderer ist bereits initialisiert oder zerstört.');
    const initialResolution = chooseRenderResolution(
      host.getBoundingClientRect().width,
      LAYOUT.stageWidth,
      globalThis.devicePixelRatio || 1,
      this.resolutionCap,
    );
    await this.app.init({
      width: LAYOUT.stageWidth,
      height: LAYOUT.stageHeight,
      backgroundAlpha: 0,
      antialias: true,
      autoDensity: true,
      resolution: initialResolution,
      preference: 'webgl',
    });
    this.app.canvas.dataset.slotCanvas = 'true';
    this.app.canvas.setAttribute('aria-hidden', 'true');
    host.querySelector('[data-render-loading]')?.remove();
    host.append(this.app.canvas);
    await this.loadTextures();
    this.app.stage.addChild(
      this.boardLayer,
      this.reelsLayer,
      this.potLayer,
      this.cardsLayer,
      this.anticipationLayer,
      this.winLayer,
      this.effectsLayer,
    );
    this.drawBoard();
    this.createReels();
    this.initialized = true;
    this.setGrid(initialGrid);
    if (this.mode === 'cards') this.prepareIdleCards();
    this.densityObserver = new DprObserver(host, LAYOUT.stageWidth, ({ cssWidth, devicePixelRatio, resolution }) => {
      if (!this.initialized || this.destroyed) return;
      this.app.canvas.dataset.cssWidth = String(Math.round(cssWidth));
      this.app.canvas.dataset.devicePixelRatio = String(devicePixelRatio);
      this.app.canvas.dataset.renderResolution = String(resolution);
      if (this.app.renderer.resolution === resolution) return;
      this.app.renderer.resize(LAYOUT.stageWidth, LAYOUT.stageHeight, resolution);
    }, this.resolutionCap);
    this.densityObserver.start();
  }

  setMode(mode: PresentationMode): void {
    if (this.mode === mode) return;
    this.mode = mode;
    if (!this.initialized) return;
    if (mode === 'cards') this.prepareIdleCards();
    else this.clearCardCovers(true);
  }

  setGrid(grid: SlotGrid, sticky: readonly GridPosition[] = []): void {
    this.assertReady();
    this.currentGrid = grid;
    this.stickyPositions = sticky;
    this.clearWinPresentation();
    for (let reel = 0; reel < 5; reel += 1) this.renderColumn(reel, grid[reel]!, sticky);
  }

  async presentSpin(result: SpinPresentationResult, options: PresentSpinOptions): Promise<void> {
    this.assertReady();
    this.cancel();
    const controller = new AbortController();
    this.controller = controller;
    const abortFromCaller = (): void => controller.abort();
    options.signal?.addEventListener('abort', abortFromCaller, { once: true });
    const reducedMotion = options.reducedMotion ?? this.defaultReducedMotion;
    // A temporary Obelisk reel remains visible during the complete win state.
    // The following spin begins with the real precomputed grid again.
    this.lingeringObelisk = null;
    const timeline = createSpinTimeline({
      mode: this.mode,
      turbo: options.turbo,
      reducedMotion,
      grid: result.grid,
      scatterBlockers: result.stickyBefore,
    });
    this.eventSink.emit({ type: 'spin-start', mode: this.mode, turbo: options.turbo });
    this.clearWinPresentation();
    try {
      if (this.mode === 'cards') this.prepareCardPresentation(result);
      else this.preparePerformancePresentation(result, timeline, options.turbo);
      await this.runSpinTimeline(result, timeline, controller.signal);
      this.setGrid(result.grid, result.stickyBefore);
      await this.presentGodEvents(result, options.turbo, reducedMotion, controller.signal);
      await this.showcaseWins(result.lineWins, options.turbo, reducedMotion, controller.signal);
      if (result.newlySticky.length > 0) await this.presentStickyReveal(result, reducedMotion, controller.signal);
      else this.setGrid(result.grid, result.stickyAfter);
      this.restoreLingeringObelisk();
      if ((result.retriggerAwarded ?? 0) > 0) {
        this.eventSink.emit({ type: 'retrigger', awarded: result.retriggerAwarded! });
      }
      if (result.potOfGreed !== undefined) {
        await this.presentPotOfGreed(result.potOfGreed.position, options.turbo, reducedMotion, controller.signal);
      }
      this.restoreLingeringObelisk();
      this.eventSink.emit({ type: 'presentation-complete' });
    } catch (error) {
      if (!isAbortError(error)) throw error;
      this.setGrid(result.grid, result.stickyAfter);
    } finally {
      this.clearSpinVisuals();
      if (this.controller === controller) this.controller = null;
      options.signal?.removeEventListener('abort', abortFromCaller);
    }
  }

  async presentReplayStep(step: ReplayPresentationStep, reducedMotion = this.defaultReducedMotion): Promise<void> {
    this.assertReady();
    this.cancel();
    const controller = new AbortController();
    this.controller = controller;
    this.lingeringObelisk = null;
    try {
      this.setGrid(step.grid, step.stickyBefore);
      if (this.mode === 'cards') {
        // Keep the prepared POG tile hidden behind the card backs. It becomes
        // visible only when its reel actually stops/reveals.
        this.preparePotOverlay(step.potOfGreed?.position ?? null, false);
        this.createCardCovers(step.stickyBefore);
        for (let reel = 0; reel < 5; reel += 1) {
          if (!reducedMotion) await this.animateCardReveal(reel, 260, controller.signal);
          this.revealStoppedReel(reel, step);
        }
      } else {
        this.preparePotOverlay(step.potOfGreed?.position ?? null, false);
      }
      await this.presentGodEvents(step, false, reducedMotion, controller.signal);
      await this.showcaseWins(step.lineWins, false, reducedMotion, controller.signal);
      if (step.newlySticky.length > 0) await this.presentStickyReveal(step, reducedMotion, controller.signal);
      else this.setGrid(step.grid, step.stickyAfter);
      this.restoreLingeringObelisk();
      if (step.potOfGreed !== undefined) {
        await this.presentPotOfGreed(step.potOfGreed.position, false, reducedMotion, controller.signal);
      }
      this.restoreLingeringObelisk();
    } catch (error) {
      if (!isAbortError(error)) throw error;
      this.setGrid(step.grid, step.stickyAfter);
    } finally {
      this.clearSpinVisuals();
      if (this.controller === controller) this.controller = null;
    }
  }

  cancel(): void {
    this.controller?.abort();
    this.controller = null;
    this.clearSpinVisuals();
  }

  destroy(): void {
    if (this.destroyed) return;
    this.destroyed = true;
    this.cancel();
    this.clearIdleWinCycle();
    this.densityObserver?.destroy();
    this.densityObserver = null;
    if (this.initialized) {
      this.app.destroy({ removeView: true }, { children: true, texture: false, textureSource: false, context: true });
    }
    this.textures.clear();
    this.initialized = false;
  }

  private assertReady(): void {
    if (!this.initialized || this.destroyed) throw new Error('Renderer ist nicht betriebsbereit.');
  }

  private async loadTextures(): Promise<void> {
    const urls = [...new Set(RUNTIME_IMAGE_URLS)];
    await Promise.all(urls.map(async (url) => {
      try {
        const texture = await Assets.load<Texture>(url);
        if (HIGH_FIDELITY_ARTWORK_URLS.has(url)) {
          texture.source.autoGenerateMipmaps = true;
          texture.source.scaleMode = 'linear';
          texture.source.update();
        }
        this.textures.set(url, texture);
      } catch {
        this.textures.set(url, Texture.EMPTY);
      }
    }));
  }

  private drawBoard(): void {
    const bounds = getBoardBounds();
    const shadow = new Graphics()
      .roundRect(bounds.x - 34, bounds.y - 34, bounds.width + 68, bounds.height + 68, 30)
      .fill({ color: 0x03060d, alpha: 0.94 })
      .stroke({ color: 0x5a3c16, width: 14, alpha: 1 });
    const frame = new Graphics()
      .roundRect(bounds.x - 24, bounds.y - 24, bounds.width + 48, bounds.height + 48, 24)
      .fill({ color: 0x100b08, alpha: 0.96 })
      .stroke({ color: 0xd3a84d, width: 6, alpha: 0.95 })
      .roundRect(bounds.x - 14, bounds.y - 14, bounds.width + 28, bounds.height + 28, 17)
      .stroke({ color: 0x71501e, width: 3, alpha: 0.8 });
    const topGem = new Graphics()
      .poly([
        LAYOUT.stageWidth / 2 - 34, 12,
        LAYOUT.stageWidth / 2, 34,
        LAYOUT.stageWidth / 2 + 34, 12,
        LAYOUT.stageWidth / 2, 0,
      ])
      .fill({ color: 0x16345f, alpha: 0.95 })
      .stroke({ color: 0xffd86b, width: 3, alpha: 0.9 });
    this.boardLayer.addChild(shadow, frame, topGem);
  }

  private createReels(): void {
    const reelHeight = getReelHeight();
    for (let reel = 0; reel < 5; reel += 1) {
      const root = new Container();
      root.position.set(
        LAYOUT.startX + reel * (LAYOUT.reelWidth + LAYOUT.columnGap),
        LAYOUT.startY,
      );
      const backdrop = new Graphics()
        .roundRect(0, 0, LAYOUT.reelWidth, reelHeight, 10)
        .fill({ color: 0x07101c, alpha: 0.98 })
        .stroke({ color: 0x88662a, width: 2, alpha: 0.72 });
      const content = new Container();
      const mask = new Graphics().roundRect(0, 0, LAYOUT.reelWidth, reelHeight, 9).fill(0xffffff);
      content.mask = mask;
      const glow = new Graphics()
        .roundRect(-5, -5, LAYOUT.reelWidth + 10, reelHeight + 10, 14)
        .stroke({ color: SCATTER_ANTICIPATION_COLOR, width: 13, alpha: 0.25 })
        .roundRect(-3, -3, LAYOUT.reelWidth + 6, reelHeight + 6, 12)
        .stroke({ color: 0xff8da4, width: 5, alpha: 0.96 });
      glow.position.copyFrom(root.position);
      glow.alpha = 0;
      root.addChild(backdrop, content, mask);
      this.reelsLayer.addChild(root);
      // This layer sits above the card covers, as in the legacy card reveal.
      this.anticipationLayer.addChild(glow);
      this.reelRoots.push(root);
      this.reelContents.push(content);
      this.reelGlows.push(glow);
    }
  }

  private renderColumn(reel: number, symbols: readonly SymbolId[], sticky: readonly GridPosition[]): void {
    const content = this.reelContents[reel];
    if (content === undefined) return;
    content.removeChildren().forEach((child) => child.destroy({ children: true }));
    content.y = 0;
    for (let row = 0; row < 3; row += 1) {
      const symbol = symbols[row];
      if (symbol === undefined) continue;
      const stickyPosition = sticky.find((position) => position.reel === reel && position.row === row);
      const isSticky = stickyPosition !== undefined;
      // Sticky positions are always displayed as the Yami-Yugi wild. This is
      // important for Pot of Greed: its stored grid retains the original
      // symbol for the payline result, but the revealed card must transform
      // into WILD after the POG reveal/award sequence.
      const cell = this.createCell(isSticky ? 'WILD' : symbol, isSticky, stickyPosition?.multiplier ?? 1);
      cell.y = row * (LAYOUT.symbolHeight + LAYOUT.rowGap);
      content.addChild(cell);
    }
  }

  private createCell(symbolId: SymbolId, sticky: boolean, multiplier = 1): Container {
    const art = SYMBOL_ARTWORK[symbolId];
    const cardStyle = getSymbolCardStyle(symbolId);
    const root = new Container();
    const isLow = art.tier === 'low';
    const isScatter = art.tier === 'scatter';
    const isWild = art.tier === 'wild';
    const surface = new Graphics()
      .roundRect(2, 2, LAYOUT.reelWidth - 4, LAYOUT.symbolHeight - 4, 12)
      .fill({ color: cardStyle.deepColor, alpha: 1 })
      .stroke({ color: 0x020305, width: 2, alpha: 0.9 });
    const material = new Graphics()
      .roundRect(5, 5, LAYOUT.reelWidth - 10, LAYOUT.symbolHeight - 10, 9)
      .fill({ color: cardStyle.surfaceColor, alpha: 0.99 })
      .rect(6, 6, LAYOUT.reelWidth - 12, 50)
      .fill({ color: isLow ? 0xffffff : cardStyle.frameColor, alpha: isLow ? 0.018 : 0.062 })
      .rect(6, 111, LAYOUT.reelWidth - 12, 39)
      .fill({ color: cardStyle.labelColor, alpha: isLow ? 0.24 : 0.58 });
    const cardBackTexture = this.textures.get(CARD_BACK_URL) ?? Texture.EMPTY;
    const watermark = cardBackTexture === Texture.EMPTY ? undefined : new Sprite(cardBackTexture);
    if (watermark !== undefined) {
      watermark.anchor.set(0.5);
      const fitted = fitContain(cardBackTexture.width, cardBackTexture.height, 82, 126);
      watermark.width = fitted.width;
      watermark.height = fitted.height;
      watermark.position.set(LAYOUT.reelWidth / 2, 75);
      watermark.tint = cardStyle.cardBackTint;
      watermark.alpha = cardStyle.cardBackAlpha;
    }
    const atmosphere = new Graphics();
    if (!isLow) {
      atmosphere
        .ellipse(LAYOUT.reelWidth / 2, 61, isScatter ? 57 : 61, isScatter ? 46 : 50)
        .fill({ color: cardStyle.frameColor, alpha: cardStyle.auraAlpha })
        .ellipse(LAYOUT.reelWidth / 2, 61, isScatter ? 42 : 47, isScatter ? 34 : 40)
        .fill({ color: cardStyle.secondaryColor, alpha: cardStyle.auraAlpha * 0.62 });
    }
    const motif = this.createCardMotif(cardStyle.motif, cardStyle.secondaryColor, isWild ? 0.19 : 0.095);
    const innerFrame = new Graphics()
      .roundRect(5, 5, LAYOUT.reelWidth - 10, LAYOUT.symbolHeight - 10, 9)
      .stroke({
        color: cardStyle.frameColor,
        width: cardStyle.frameWidth,
        alpha: cardStyle.frameAlpha,
      })
      .roundRect(8, 8, LAYOUT.reelWidth - 16, LAYOUT.symbolHeight - 16, 7)
      .stroke({ color: isWild ? 0xffedaf : 0xffffff, width: 1, alpha: isLow ? 0.035 : isWild ? 0.22 : 0.1 });
    if (isWild) {
      innerFrame
        .roundRect(11, 11, LAYOUT.reelWidth - 22, LAYOUT.symbolHeight - 22, 5)
        .stroke({ color: cardStyle.secondaryColor, width: 1, alpha: 0.34 });
    }
    const sheen = new Graphics()
      .moveTo(16, 8)
      .quadraticCurveTo(LAYOUT.reelWidth / 2, 3, LAYOUT.reelWidth - 16, 8)
      .stroke({ color: 0xffffff, width: 1.5, alpha: isLow ? 0.065 : 0.16 });
    root.addChild(surface, material);
    if (watermark !== undefined) root.addChild(watermark);
    root.addChild(motif, atmosphere, innerFrame, sheen);
    const texture = art.imageUrl === undefined ? undefined : this.textures.get(art.imageUrl);
    if (texture !== undefined && texture !== Texture.EMPTY) {
      const sprite = new Sprite(texture);
      sprite.anchor.set(0.5);
      const fitted = fitContain(texture.width, texture.height, cardStyle.artWidth, cardStyle.artHeight);
      sprite.width = fitted.width;
      sprite.height = fitted.height;
      sprite.position.set(LAYOUT.reelWidth / 2, 61);
      root.addChild(sprite);
    } else {
      const low = new Text({
        text: art.shortLabel,
        style: textStyle(64, art.accent),
        resolution: TEXT_TEXTURE_RESOLUTION,
      });
      low.anchor.set(0.5);
      low.position.set(LAYOUT.reelWidth / 2, 59);
      root.addChild(low);
    }
    const labelRule = new Graphics()
      .moveTo(22, 116)
      .lineTo(LAYOUT.reelWidth - 22, 116)
      .stroke({ color: cardStyle.frameColor, width: 1, alpha: isLow ? 0.14 : 0.3 });
    const label = new Text({
      text: art.tier === 'low' ? art.name.toUpperCase() : art.shortLabel,
      style: textStyle(12, isScatter ? 0xffd5d9 : 0xe8d9ad, '600'),
      resolution: TEXT_TEXTURE_RESOLUTION,
    });
    label.anchor.set(0.5);
    label.position.set(LAYOUT.reelWidth / 2, 136);
    label.alpha = 0.92;
    root.addChild(labelRule, label);
    if (sticky) {
      const lock = new Graphics()
        .roundRect(4, 4, LAYOUT.reelWidth - 8, LAYOUT.symbolHeight - 8, 11)
        .stroke({ color: 0xffdb62, width: 5, alpha: 0.98 });
      const tag = new Text({
        text: 'STICKY',
        style: textStyle(12, 0x1c1202),
        resolution: TEXT_TEXTURE_RESOLUTION,
      });
      const plate = new Graphics().roundRect(45, 5, 70, 23, 8).fill({ color: 0xffd45f, alpha: 0.96 });
      tag.anchor.set(0.5);
      tag.position.set(80, 16);
      root.addChild(lock, plate, tag);
      if (multiplier > 1) {
        const multiplierPlate = new Graphics()
          .roundRect(LAYOUT.reelWidth - 50, 5, 43, 25, 8)
          .fill({ color: 0x130a02, alpha: 0.96 })
          .stroke({ color: 0xffe38a, width: 2, alpha: 1 });
        const multiplierLabel = new Text({
          text: `${multiplier}×`, style: textStyle(14, 0xffed9b), resolution: TEXT_TEXTURE_RESOLUTION,
        });
        multiplierLabel.anchor.set(0.5);
        multiplierLabel.position.set(LAYOUT.reelWidth - 28.5, 17.5);
        root.addChild(multiplierPlate, multiplierLabel);
      }
    }
    return root;
  }

  /** A complete, opaque Obelisk WILD tile used only by the selected temporary reel. */
  private createObeliskWildCell(multiplier = 1): Container {
    const root = new Container();
    root.label = 'obelisk-wild';
    const surface = new Graphics()
      .roundRect(2, 2, LAYOUT.reelWidth - 4, LAYOUT.symbolHeight - 4, 12)
      .fill({ color: 0x03091b, alpha: 1 })
      .stroke({ color: 0x02030a, width: 2, alpha: 1 });
    const material = new Graphics()
      .roundRect(5, 5, LAYOUT.reelWidth - 10, LAYOUT.symbolHeight - 10, 9)
      .fill({ color: 0x0a2254, alpha: 1 })
      .rect(7, 7, LAYOUT.reelWidth - 14, 58)
      .fill({ color: 0x397ee8, alpha: 0.18 })
      .rect(7, 110, LAYOUT.reelWidth - 14, 41)
      .fill({ color: 0x020715, alpha: 0.68 });
    const monument = new Graphics()
      .moveTo(LAYOUT.reelWidth / 2, 12).lineTo(27, 105).lineTo(LAYOUT.reelWidth - 27, 105).closePath()
      .stroke({ color: 0x6fa9ff, width: 1.5, alpha: 0.24 })
      .circle(LAYOUT.reelWidth / 2, 57, 45)
      .stroke({ color: 0x4d91ff, width: 1.4, alpha: 0.28 })
      .circle(LAYOUT.reelWidth / 2, 57, 34)
      .stroke({ color: 0xb9d8ff, width: 1, alpha: 0.15 });
    const aura = new Graphics()
      .ellipse(LAYOUT.reelWidth / 2, 59, 63, 51)
      .fill({ color: 0x357cff, alpha: 0.18 })
      .ellipse(LAYOUT.reelWidth / 2, 59, 48, 39)
      .fill({ color: 0x8cbcff, alpha: 0.10 });
    const frame = new Graphics()
      .roundRect(5, 5, LAYOUT.reelWidth - 10, LAYOUT.symbolHeight - 10, 9)
      .stroke({ color: 0x62a6ff, width: 3.2, alpha: 0.98 })
      .roundRect(9, 9, LAYOUT.reelWidth - 18, LAYOUT.symbolHeight - 18, 7)
      .stroke({ color: 0xc8e2ff, width: 1.2, alpha: 0.55 });
    root.addChild(surface, material, monument, aura);
    const texture = this.textures.get(EGYPTIAN_GOD_URLS.OBELISK) ?? Texture.EMPTY;
    if (texture !== Texture.EMPTY) {
      const sprite = new Sprite(texture);
      sprite.anchor.set(0.5);
      const fitted = fitContain(texture.width, texture.height, LAYOUT.reelWidth - 25, 103);
      sprite.width = fitted.width;
      sprite.height = fitted.height;
      sprite.position.set(LAYOUT.reelWidth / 2, 58);
      root.addChild(sprite);
    } else {
      const fallback = new Text({ text: 'OBELISK', style: textStyle(19, 0x91c5ff), resolution: TEXT_TEXTURE_RESOLUTION });
      fallback.anchor.set(0.5);
      fallback.position.set(LAYOUT.reelWidth / 2, 58);
      root.addChild(fallback);
    }
    const labelRule = new Graphics().moveTo(20, 116).lineTo(LAYOUT.reelWidth - 20, 116)
      .stroke({ color: 0x77b5ff, width: 1.2, alpha: 0.52 });
    const label = new Text({ text: 'WILD', style: textStyle(13, 0xddeeff, '600'), resolution: TEXT_TEXTURE_RESOLUTION });
    label.anchor.set(0.5);
    label.position.set(LAYOUT.reelWidth / 2, 136);
    root.addChild(frame, labelRule, label);
    if (multiplier > 1) {
      const multiplierPlate = new Graphics()
        .roundRect(LAYOUT.reelWidth - 54, 7, 46, 27, 8)
        .fill({ color: 0x020713, alpha: 0.98 })
        .stroke({ color: 0xb9dcff, width: 2, alpha: 1 });
      const multiplierLabel = new Text({
        text: `${multiplier}×`, style: textStyle(14, 0xe7f4ff), resolution: TEXT_TEXTURE_RESOLUTION,
      });
      multiplierLabel.anchor.set(0.5);
      multiplierLabel.position.set(LAYOUT.reelWidth - 31, 20.5);
      root.addChild(multiplierPlate, multiplierLabel);
    }
    const temporary = new Text({ text: 'GÖTTER-WILD', style: textStyle(8, 0x9bc9ff, '600'), resolution: TEXT_TEXTURE_RESOLUTION });
    temporary.anchor.set(0.5);
    temporary.position.set(LAYOUT.reelWidth / 2, 17);
    root.addChild(temporary);
    return root;
  }

  private async presentGodEvents(
    result: SpinPresentationResult,
    turbo: boolean,
    reducedMotion: boolean,
    signal: AbortSignal,
  ): Promise<void> {
    if (!result.godEvents?.length) return;
    const sticky = result.stickyBefore.map((position) => ({ ...position, multiplier: position.multiplier ?? 1 }));
    for (const event of [...result.godEvents].sort((left, right) => left.order - right.order)) {
      await this.presentGodCard(event, turbo, reducedMotion, signal);
      if (event.god === 'RA') {
        await this.presentRaBoardConfirmation(event, turbo, reducedMotion, signal);
      } else if (event.god === 'SLIFER') {
        for (const target of event.sliferTargets ?? []) {
          await this.presentSliferTarget(target, turbo, reducedMotion, signal);
          const index = sticky.findIndex((entry) => entry.reel === target.reel && entry.row === target.row);
          const next = { reel: target.reel, row: target.row, multiplier: target.multiplier };
          if (index >= 0) sticky[index] = next;
          else sticky.push(next);
          this.setGrid(result.grid, sticky);
          await this.presentSliferImpact(target, turbo, reducedMotion, signal);
        }
      } else if (event.god === 'OBELISK' && event.obeliskReel !== undefined) {
        // Current-spin natural wilds are mathematically sticky during the god
        // phase, even though their regular sticky reveal is presented later.
        const obeliskSticky = [...sticky];
        for (const position of result.newlySticky) {
          if (obeliskSticky.some((entry) => entry.reel === position.reel && entry.row === position.row)) continue;
          const persisted = result.stickyAfter.find((entry) => (
            entry.reel === position.reel && entry.row === position.row
          ));
          obeliskSticky.push({ ...position, multiplier: persisted?.multiplier ?? 1 });
        }
        const plan = createObeliskVisualPlan(event, result.grid, obeliskSticky, result.potOfGreed?.position ?? null);
        if (plan !== null) {
          await this.presentObeliskTransformation(result.grid, obeliskSticky, plan, turbo, reducedMotion, signal);
          this.lingeringObelisk = Object.freeze({ plan, grid: result.grid, sticky: Object.freeze(obeliskSticky) });
        }
      }
    }
  }

  private async presentGodCard(
    event: GodPresentationEvent,
    turbo: boolean,
    reducedMotion: boolean,
    signal: AbortSignal,
  ): Promise<void> {
    this.eventSink.emit({ type: 'god-activation', god: event.god });
    const colors = { RA: 0xffd35c, SLIFER: 0xef4b4b, OBELISK: 0x4b8fff } as const;
    const names = { RA: 'DER GEFÜGELTE DRACHE DES RA', SLIFER: 'SLIFER, DER HIMMELSDRACHE', OBELISK: 'OBELISK, DER PEINIGER' } as const;
    const copy = getGodCardCopy(event);
    const timing = getGodCardTiming(turbo, reducedMotion);
    const overlay = new Container();
    const shade = new Graphics().rect(0, 0, LAYOUT.stageWidth, LAYOUT.stageHeight).fill({ color: 0x02030a, alpha: 0.72 });
    const frame = new Graphics().roundRect(280, 30, 400, 420, 22)
      .fill({ color: 0x090b16, alpha: 0.985 })
      .stroke({ color: colors[event.god], width: 4, alpha: 0.95 })
      .roundRect(288, 38, 384, 404, 17)
      .stroke({ color: 0xfff0c7, width: 1, alpha: 0.18 });
    const texture = this.textures.get(EGYPTIAN_GOD_URLS[event.god]) ?? Texture.EMPTY;
    const sprite = new Sprite(texture);
    sprite.anchor.set(0.5);
    const fitted = fitContain(texture.width, texture.height, 306, 218);
    sprite.width = fitted.width; sprite.height = fitted.height; sprite.position.set(480, 151);
    const title = new Text({ text: names[event.god], style: textStyle(18, colors[event.god]), resolution: TEXT_TEXTURE_RESOLUTION });
    title.anchor.set(0.5); title.position.set(480, 278);
    const divider = new Graphics().moveTo(333, 301).lineTo(627, 301)
      .stroke({ color: colors[event.god], width: 1.5, alpha: 0.55 });
    const primary = new Text({ text: copy.primary, style: textStyle(event.god === 'RA' ? 27 : 22, event.god === 'RA' ? 0xffdd73 : 0xfff1c7), resolution: TEXT_TEXTURE_RESOLUTION });
    primary.anchor.set(0.5); primary.position.set(480, 333);
    const secondary = new Text({ text: copy.secondary, style: textStyle(16, event.god === 'RA' ? 0xffe7a3 : colors[event.god], '600'), resolution: TEXT_TEXTURE_RESOLUTION });
    secondary.anchor.set(0.5); secondary.position.set(480, 373);
    overlay.addChild(shade, frame, sprite, title, divider, primary, secondary);
    if (copy.tertiary !== undefined) {
      const tertiary = new Text({ text: copy.tertiary, style: textStyle(13, 0xdccca8, '600'), resolution: TEXT_TEXTURE_RESOLUTION });
      tertiary.anchor.set(0.5); tertiary.position.set(480, 407);
      overlay.addChild(tertiary);
    }
    overlay.alpha = timing.enterMs === 0 ? 1 : 0;
    overlay.scale.set(timing.enterMs === 0 ? 1 : 0.965);
    this.effectsLayer.addChild(overlay);
    await this.animateEffect(timing.enterMs, (progress) => {
      overlay.alpha = easeOutCubic(progress);
      overlay.scale.set(0.965 + easeOutCubic(progress) * 0.035);
    }, signal);
    await wait(timing.holdMs, signal);
    await this.animateEffect(timing.exitMs, (progress) => {
      overlay.alpha = 1 - easeInOutCubic(progress);
      overlay.scale.set(1 + progress * 0.018);
    }, signal);
    overlay.destroy({ children: true });
  }

  private async presentRaBoardConfirmation(
    event: GodPresentationEvent,
    turbo: boolean,
    reducedMotion: boolean,
    signal: AbortSignal,
  ): Promise<void> {
    const copy = getGodCardCopy(event);
    if (copy.boardConfirmation === undefined) return;
    const bounds = getBoardBounds();
    const root = new Container();
    root.label = 'ra-board-confirmation';
    const sunlight = new Graphics()
      .roundRect(bounds.x - 12, bounds.y - 12, bounds.width + 24, bounds.height + 24, 18)
      .fill({ color: 0xffbd32, alpha: 0.13 })
      .stroke({ color: 0xffdc73, width: 8, alpha: 0.9 });
    const centerX = bounds.x + bounds.width / 2;
    const centerY = bounds.y + bounds.height / 2;
    const rays = new Graphics();
    for (let ray = 0; ray < 16; ray += 1) {
      const angle = (Math.PI * 2 * ray) / 16;
      rays.moveTo(centerX + Math.cos(angle) * 72, centerY + Math.sin(angle) * 72)
        .lineTo(centerX + Math.cos(angle) * 196, centerY + Math.sin(angle) * 196)
        .stroke({ color: 0xffd45f, width: ray % 2 === 0 ? 5 : 2.5, alpha: 0.5 });
    }
    const plate = new Graphics().roundRect(centerX - 145, centerY - 28, 290, 56, 14)
      .fill({ color: 0x241300, alpha: 0.96 }).stroke({ color: 0xffda66, width: 3, alpha: 1 });
    const label = new Text({ text: copy.boardConfirmation, style: textStyle(21, 0xffe79b), resolution: TEXT_TEXTURE_RESOLUTION });
    label.anchor.set(0.5); label.position.set(centerX, centerY);
    root.addChild(sunlight, rays, plate, label);
    this.effectsLayer.addChild(root);
    const duration = reducedMotion ? 750 : turbo ? 850 : 1_150;
    await this.animateEffect(duration, (progress) => {
      const appear = Math.min(1, progress * 4);
      const leave = progress > 0.72 ? 1 - (progress - 0.72) / 0.28 : 1;
      root.alpha = appear * leave;
      if (!reducedMotion) {
        rays.rotation = progress * 0.18;
        sunlight.alpha = 0.55 + Math.sin(progress * Math.PI) * 0.45;
      }
    }, signal);
    root.destroy({ children: true });
  }

  private async animateEffect(
    durationMs: number,
    update: (progress: number) => void,
    signal: AbortSignal,
  ): Promise<void> {
    if (durationMs <= 0) {
      update(1);
      return;
    }
    const startedAt = now();
    await new Promise<void>((resolve, reject) => {
      let frame = 0;
      let settled = false;
      const cancel = (): void => {
        if (settled) return;
        settled = true;
        if (frame !== 0) globalThis.cancelAnimationFrame(frame);
        signal.removeEventListener('abort', cancel);
        reject(new DOMException('Göttereffekt abgebrochen.', 'AbortError'));
      };
      const tick = (): void => {
        const progress = Math.min(1, (now() - startedAt) / Math.max(1, durationMs));
        update(progress);
        if (progress >= 1) {
          settled = true;
          signal.removeEventListener('abort', cancel);
          resolve();
          return;
        }
        frame = globalThis.requestAnimationFrame(tick);
      };
      signal.addEventListener('abort', cancel, { once: true });
      frame = globalThis.requestAnimationFrame(tick);
    });
  }

  /** Charges the selected Slifer cell before its persistent state is changed. */
  private async presentSliferTarget(
    target: NonNullable<GodPresentationEvent['sliferTargets']>[number],
    turbo: boolean,
    reducedMotion: boolean,
    signal: AbortSignal,
  ): Promise<void> {
    const center = getCellCenter(target);
    const root = new Container();
    root.position.set(center.x, center.y);
    root.label = 'slifer-charge';
    const veil = new Graphics().roundRect(
      -LAYOUT.reelWidth / 2 + 3, -LAYOUT.symbolHeight / 2 + 3,
      LAYOUT.reelWidth - 6, LAYOUT.symbolHeight - 6, 11,
    ).fill({ color: 0x8d0714, alpha: 0.22 });
    const rings = new Graphics()
      .circle(0, 0, 68).stroke({ color: 0xff4055, width: 6, alpha: 0.92 })
      .circle(0, 0, 51).stroke({ color: 0xffb0a8, width: 2, alpha: 0.76 });
    const bolt = new Graphics()
      .moveTo(0, -LAYOUT.symbolHeight / 2 - 52)
      .lineTo(-17, -51).lineTo(9, -31).lineTo(-8, -10).lineTo(12, 12).lineTo(0, 0)
      .stroke({ color: 0xfff0d0, width: 9, alpha: 0.95, cap: 'round', join: 'round' })
      .moveTo(0, -LAYOUT.symbolHeight / 2 - 52)
      .lineTo(-17, -51).lineTo(9, -31).lineTo(-8, -10).lineTo(12, 12).lineTo(0, 0)
      .stroke({ color: 0xff2d4c, width: 4, alpha: 1, cap: 'round', join: 'round' });
    root.addChild(veil, rings, bolt);
    this.effectsLayer.addChild(root);
    const duration = reducedMotion ? 90 : turbo ? 145 : 330;
    await this.animateEffect(duration, (progress) => {
      const eased = easeOutCubic(progress);
      rings.scale.set(1.28 - eased * 0.28);
      rings.rotation = progress * 0.65;
      rings.alpha = 0.35 + Math.sin(progress * Math.PI) * 0.65;
      bolt.alpha = progress < 0.2 ? progress * 5 : 0.72 + Math.sin(progress * 31) * 0.24;
      veil.alpha = 0.12 + Math.sin(progress * Math.PI) * 0.28;
    }, signal);
    root.destroy({ children: true });
  }

  /** Confirms the precise Slifer target and its old/new multiplier after impact. */
  private async presentSliferImpact(
    target: NonNullable<GodPresentationEvent['sliferTargets']>[number],
    turbo: boolean,
    reducedMotion: boolean,
    signal: AbortSignal,
  ): Promise<void> {
    const center = getCellCenter(target);
    const root = new Container();
    root.position.set(center.x, center.y);
    root.label = 'slifer-impact';
    const burst = new Graphics()
      .circle(0, 0, 22).fill({ color: 0xfff0c8, alpha: 0.9 })
      .circle(0, 0, 48).stroke({ color: 0xff3955, width: 8, alpha: 0.95 })
      .circle(0, 0, 68).stroke({ color: 0xffa1aa, width: 3, alpha: 0.72 });
    const detail = target.previousMultiplier === 0
      ? `NEUES ${target.multiplier}× WILD`
      : `${target.previousMultiplier}×  →  ${target.multiplier}×`;
    const plate = new Graphics().roundRect(-75, 43, 150, 31, 10)
      .fill({ color: 0x170207, alpha: 0.96 }).stroke({ color: 0xff5b68, width: 2, alpha: 0.95 });
    const label = new Text({ text: detail, style: textStyle(14, 0xffe4cf), resolution: TEXT_TEXTURE_RESOLUTION });
    label.anchor.set(0.5);
    label.position.set(0, 58.5);
    root.addChild(burst, plate, label);
    this.effectsLayer.addChild(root);
    const duration = reducedMotion ? 110 : turbo ? 180 : 420;
    await this.animateEffect(duration, (progress) => {
      const appear = Math.min(1, progress * 4);
      const leave = progress > 0.72 ? 1 - (progress - 0.72) / 0.28 : 1;
      root.alpha = appear * leave;
      burst.scale.set(0.55 + easeOutCubic(progress) * 0.75);
      plate.y = 8 * (1 - easeOutCubic(progress));
      label.y = 58.5 + plate.y;
    }, signal);
    root.destroy({ children: true });
  }

  private renderObeliskColumn(
    grid: SlotGrid,
    sticky: readonly GridPosition[],
    plan: ObeliskVisualPlan,
    reel: number,
    visibleCells: ReadonlySet<string> = new Set(plan.cells.map((cell) => `${cell.reel}:${cell.row}`)),
  ): void {
    const content = this.reelContents[reel];
    if (content === undefined) return;
    content.removeChildren().forEach((child) => child.destroy({ children: true }));
    content.y = 0;
    for (let row = 0; row < 3; row += 1) {
      const obeliskCell = plan.cells.find((cell) => cell.reel === reel && cell.row === row);
      const stickyPosition = sticky.find((position) => position.reel === reel && position.row === row);
      const symbol = grid[reel]![row]!;
      const cell = obeliskCell !== undefined && visibleCells.has(`${reel}:${row}`)
        ? this.createObeliskWildCell(obeliskCell.multiplier)
        : this.createCell(stickyPosition === undefined ? symbol : 'WILD', stickyPosition !== undefined, stickyPosition?.multiplier ?? 1);
      cell.y = row * (LAYOUT.symbolHeight + LAYOUT.rowGap);
      content.addChild(cell);
    }
  }

  private renderObeliskPlan(
    grid: SlotGrid,
    sticky: readonly GridPosition[],
    plan: ObeliskVisualPlan,
    visibleCells?: ReadonlySet<string>,
  ): void {
    for (const reel of plan.reels) this.renderObeliskColumn(grid, sticky, plan, reel, visibleCells);
  }

  private async presentObeliskTransformation(
    grid: SlotGrid,
    sticky: readonly GridPosition[],
    plan: ObeliskVisualPlan,
    turbo: boolean,
    reducedMotion: boolean,
    signal: AbortSignal,
  ): Promise<void> {
    const firstReel = Math.min(...plan.reels);
    const lastReel = Math.max(...plan.reels);
    const bounds = getCellRect({ reel: firstReel, row: 0 });
    const lastBounds = getCellRect({ reel: lastReel, row: 0 });
    const reelHeight = getReelHeight();
    const columnEffect = new Container();
    columnEffect.label = 'obelisk-column-effect';
    const monoliths: Graphics[] = [];
    for (const reel of plan.reels) {
      const reelBounds = getCellRect({ reel, row: 0 });
      const monolith = new Graphics()
        .roundRect(reelBounds.x - 7, reelBounds.y - 9, reelBounds.width + 14, reelHeight + 18, 15)
        .fill({ color: 0x0d3d9c, alpha: 0.18 })
        .stroke({ color: 0x75b5ff, width: 8, alpha: 0.9 })
        .roundRect(reelBounds.x - 2, reelBounds.y - 4, reelBounds.width + 4, reelHeight + 8, 12)
        .stroke({ color: 0xd4eaff, width: 2, alpha: 0.72 });
      monoliths.push(monolith);
      columnEffect.addChild(monolith);
    }
    this.effectsLayer.addChild(columnEffect);
    const chargeDuration = reducedMotion ? 80 : turbo ? 120 : 300;
    await this.animateEffect(chargeDuration, (progress) => {
      columnEffect.alpha = 0.3 + Math.sin(progress * Math.PI) * 0.7;
      for (const monolith of monoliths) monolith.alpha = 0.55 + Math.sin(progress * Math.PI) * 0.45;
    }, signal);
    const visibleCells = new Set<string>();
    for (const transformed of [...plan.cells].sort((left, right) => left.reel - right.reel || left.row - right.row)) {
      const center = getCellCenter(transformed);
      const cellBounds = getCellRect(transformed);
      const impact = new Graphics()
        .circle(center.x, center.y, 18).fill({ color: 0xe8f5ff, alpha: 0.94 })
        .circle(center.x, center.y, 61).stroke({ color: 0x4e9cff, width: 9, alpha: 0.95 })
        .moveTo(cellBounds.x - 3, center.y).lineTo(cellBounds.x + cellBounds.width + 3, center.y)
        .stroke({ color: 0xb9dcff, width: 5, alpha: 0.8 });
      this.effectsLayer.addChild(impact);
      const impactDuration = reducedMotion ? 45 : turbo ? 75 : 165;
      await this.animateEffect(impactDuration, (progress) => {
        impact.alpha = 1 - easeOutCubic(progress);
        impact.scale.set(0.68 + easeOutCubic(progress) * 0.5);
      }, signal);
      visibleCells.add(`${transformed.reel}:${transformed.row}`);
      this.renderObeliskPlan(grid, sticky, plan, visibleCells);
      impact.destroy();
    }
    const seal = new Text({
      text: plan.mode === 'full-grid-multiplier'
        ? `OBELISK-STUFE ${plan.level} · MULTIPLIKATOR +1`
        : `OBELISK-STUFE ${plan.level} · ${plan.cells.length} GÖTTER-WILDS`,
      style: textStyle(13, 0xe3f2ff), resolution: TEXT_TEXTURE_RESOLUTION,
    });
    seal.anchor.set(0.5);
    seal.position.set((bounds.x + lastBounds.x + lastBounds.width) / 2, bounds.y + reelHeight / 2);
    const sealPlateWidth = Math.max(210, Math.min(390, lastBounds.x + lastBounds.width - bounds.x - 12));
    const sealPlate = new Graphics().roundRect(seal.x - sealPlateWidth / 2, seal.y - 18, sealPlateWidth, 36, 10)
      .fill({ color: 0x020817, alpha: 0.94 }).stroke({ color: 0x69aaff, width: 2, alpha: 0.95 });
    columnEffect.addChild(sealPlate, seal);
    await wait(reducedMotion ? 90 : turbo ? 130 : 300, signal);
    columnEffect.destroy({ children: true });
    this.renderObeliskPlan(grid, sticky, plan);
  }

  private restoreLingeringObelisk(): void {
    if (this.lingeringObelisk === null) return;
    this.renderObeliskPlan(
      this.lingeringObelisk.grid,
      this.lingeringObelisk.sticky,
      this.lingeringObelisk.plan,
    );
  }

  private createCardMotif(
    motif: ReturnType<typeof getSymbolCardStyle>['motif'],
    color: number,
    alpha: number,
  ): Graphics {
    const graphic = new Graphics();
    const center = LAYOUT.reelWidth / 2;
    if (motif === 'arcane') {
      graphic.circle(center, 61, 42).stroke({ color, width: 1, alpha });
      graphic.circle(center, 61, 31).stroke({ color, width: 1, alpha: alpha * 0.72 });
      graphic.moveTo(center - 42, 61).lineTo(center + 42, 61)
        .stroke({ color, width: 1, alpha: alpha * 0.55 });
    } else if (motif === 'solar') {
      graphic.circle(center, 57, 37).stroke({ color, width: 1.2, alpha });
      graphic.circle(center, 57, 7).fill({ color, alpha: alpha * 0.6 });
      for (let ray = 0; ray < 8; ray += 1) {
        const angle = (Math.PI * 2 * ray) / 8;
        graphic.moveTo(center + Math.cos(angle) * 41, 57 + Math.sin(angle) * 41)
          .lineTo(center + Math.cos(angle) * 48, 57 + Math.sin(angle) * 48)
          .stroke({ color, width: 1, alpha: alpha * 0.7 });
      }
    } else if (motif === 'pyramid') {
      graphic.moveTo(center, 19).lineTo(27, 105).lineTo(LAYOUT.reelWidth - 27, 105).lineTo(center, 19)
        .stroke({ color, width: 1.2, alpha });
      graphic.moveTo(center, 34).lineTo(43, 93).lineTo(LAYOUT.reelWidth - 43, 93).lineTo(center, 34)
        .stroke({ color, width: 1, alpha: alpha * 0.72 });
      graphic.circle(center, 57, 15).stroke({ color, width: 1, alpha: alpha * 0.8 });
    }
    return graphic;
  }

  private prepareCardPresentation(result: SpinPresentationResult): void {
    this.clearPotOverlay();
    this.setGrid(result.grid, result.stickyBefore);
    // The POG result is known mathematically, but must not be visible before
    // the corresponding card/reel reveal.
    this.preparePotOverlay(result.potOfGreed?.position ?? null, false);
    this.createCardCovers(result.stickyBefore);
    this.eventSink.emit({ type: 'shuffle', mode: 'cards' });
  }

  private prepareIdleCards(): void {
    this.createCardCovers(this.stickyPositions);
  }

  private createCardCovers(sticky: readonly GridPosition[]): void {
    this.clearCardCovers(true);
    const stickyKeys = new Set(sticky.map(gridPositionKey));
    const texture = this.textures.get(CARD_BACK_URL) ?? Texture.EMPTY;
    for (let reel = 0; reel < 5; reel += 1) {
      for (let row = 0; row < 3; row += 1) {
        const key = `${reel}:${row}`;
        if (stickyKeys.has(key)) continue;
        const symbol = this.reelContents[reel]?.children[row];
        if (symbol !== undefined) symbol.alpha = 0;
        this.coveredCellKeys.add(key);
        const rect = getCellRect({ reel, row });
        const cardRoot = new Container();
        cardRoot.label = `card:${reel}:${row}`;
        cardRoot.position.set(rect.x + rect.width / 2, rect.y + rect.height / 2);
        const card = texture === Texture.EMPTY
          ? new Graphics().roundRect(-56, -70, 112, 140, 10).fill(0x192a54).stroke({ color: 0xd9b756, width: 4 })
          : new Sprite(texture);
        if (card instanceof Sprite) {
          card.anchor.set(0.5);
          const fitted = fitContain(texture.width, texture.height, rect.width - 16, rect.height - 12);
          card.width = fitted.width;
          card.height = fitted.height;
        }
        cardRoot.addChild(card);
        this.cardsLayer.addChild(cardRoot);
      }
    }
    this.syncCardCoverState();
  }

  private preparePerformancePresentation(
    result: SpinPresentationResult,
    timeline: SpinTimeline,
    turbo: boolean,
  ): void {
    this.clearPotOverlay();
    this.preparePotOverlay(result.potOfGreed?.position ?? null, false);
    this.cardsLayer.removeChildren().forEach((child) => child.destroy({ children: true }));
    this.performanceStrips = timeline.reelStops.map((stop) => {
      const content = this.reelContents[stop.reel]!;
      content.removeChildren().forEach((child) => child.destroy({ children: true }));
      const strip = new Container();
      const fillerCount = (turbo ? 8 : 13) + stop.reel * 2;
      const entries: SymbolId[] = [];
      for (let index = 0; index < fillerCount; index += 1) {
        entries.push(PRESENTATION_FILLER[(index + stop.reel * 3) % PRESENTATION_FILLER.length]!);
      }
      entries.push(...result.grid[stop.reel]!);
      entries.forEach((symbol, index) => {
        const cell = this.createCell(symbol, false);
        cell.y = index * (LAYOUT.symbolHeight + LAYOUT.rowGap);
        strip.addChild(cell);
      });
      content.addChild(strip);
      const distance = fillerCount * (LAYOUT.symbolHeight + LAYOUT.rowGap);
      const blur = new BlurFilter({ strength: turbo ? 4 : 7, quality: 2 });
      strip.filters = [blur];
      return { root: strip, distance, stopAtMs: stop.atMs };
    });
  }

  private async runSpinTimeline(
    result: SpinPresentationResult,
    timeline: SpinTimeline,
    signal: AbortSignal,
  ): Promise<void> {
    const startedAt = now();
    const activeAnticipationReels = new Set<number>();
    const updatePerformance = (): void => {
      const elapsed = now() - startedAt;
      this.performanceStrips.forEach((strip) => {
        const progress = Math.min(1, elapsed / Math.max(1, strip.stopAtMs));
        strip.root.y = -strip.distance * easeOutCubic(progress);
      });
    };
    const updateAnticipation = (): void => {
      const elapsed = now() - startedAt;
      const pulse = timeline.reducedMotion ? 0.84 : 0.64 + Math.sin(elapsed / 92) * 0.2;
      activeAnticipationReels.forEach((reel) => {
        const glow = this.reelGlows[reel];
        if (glow !== undefined) glow.alpha = pulse;
      });
    };
    if (this.mode === 'performance' && !timeline.reducedMotion) this.app.ticker.add(updatePerformance);
    this.app.ticker.add(updateAnticipation);
    const timed: TimedRendererEvent[] = [];
    timeline.reelStops.forEach((stop) => {
      if (stop.anticipationAtMs !== undefined) {
        timed.push({
          atMs: stop.anticipationAtMs, type: 'anticipation', reel: stop.reel,
          scatterStage: stop.scatterStage, hasScatter: stop.hasScatter,
        });
      }
      if (stop.revealAtMs !== undefined) {
        timed.push({
          atMs: stop.revealAtMs, type: 'reveal-start', reel: stop.reel,
          scatterStage: stop.scatterStage, hasScatter: stop.hasScatter,
        });
      }
      timed.push({
        atMs: stop.atMs, type: 'stop', reel: stop.reel,
        scatterStage: stop.scatterStage, hasScatter: stop.hasScatter,
      });
    });
    const eventOrder = { anticipation: 0, 'reveal-start': 1, stop: 2 } as const;
    timed.sort((left, right) => left.atMs - right.atMs || eventOrder[left.type] - eventOrder[right.type]);
    try {
      if (timeline.dealCompleteAtMs !== undefined) {
        await wait(timeline.dealCompleteAtMs, signal);
        this.eventSink.emit({ type: 'deal-complete', mode: 'cards' });
      }
      for (const entry of timed) {
        await wait(Math.max(0, entry.atMs - (now() - startedAt)), signal);
        if (entry.type === 'anticipation') {
          activeAnticipationReels.add(entry.reel);
          this.reelGlows[entry.reel]!.alpha = 0.84;
          this.app.canvas.dataset.scatterAnticipation = 'active';
          this.eventSink.emit({
            type: 'anticipation-start', reel: entry.reel,
            stage: entry.scatterStage >= 3 ? 3 : 2,
          });
          continue;
        }
        if (entry.type === 'reveal-start') {
          this.eventSink.emit({ type: 'reel-reveal-start', reel: entry.reel });
          if (this.mode === 'cards' && !timeline.reducedMotion) {
            await this.animateCardReveal(entry.reel, timeline.turbo ? 150 : 260, signal);
          }
          continue;
        }
        this.revealStoppedReel(entry.reel, result);
        activeAnticipationReels.delete(entry.reel);
        this.reelGlows[entry.reel]!.alpha = 0;
        if (activeAnticipationReels.size === 0) delete this.app.canvas.dataset.scatterAnticipation;
        const hasNewWild = result.newlySticky.some((position) => position.reel === entry.reel);
        this.eventSink.emit({
          type: 'reel-stop', reel: entry.reel, scatterStage: entry.scatterStage,
          hasScatter: entry.hasScatter, hasNewWild,
        });
      }
      await wait(Math.max(0, timeline.settleCompleteAtMs - (now() - startedAt)), signal);
    } finally {
      this.app.ticker.remove(updatePerformance);
      this.app.ticker.remove(updateAnticipation);
      activeAnticipationReels.clear();
      delete this.app.canvas.dataset.scatterAnticipation;
    }
  }

  private revealStoppedReel(reel: number, result: SpinPresentationResult): void {
    if (this.mode === 'cards') {
      const cards = this.cardsLayer.children.filter((child) => child.label.startsWith(`card:${reel}:`));
      cards.forEach((card) => {
        card.scale.x = 0.08;
        card.alpha = 0;
        card.destroy({ children: true });
      });
      for (let row = 0; row < 3; row += 1) this.coveredCellKeys.delete(`${reel}:${row}`);
      this.syncCardCoverState();
    }
    this.performanceStrips[reel]?.root.destroy({ children: true });
    this.renderColumn(reel, result.grid[reel]!, result.stickyBefore);
    if (this.potOverlayPosition?.reel === reel) this.setPotOverlayVisible(true);
    const content = this.reelContents[reel]!;
    content.y = -7;
    globalThis.setTimeout(() => { if (!this.destroyed) content.y = 0; }, 90);
  }

  /**
   * Reveals a reel at the same moment the legacy card flip starts. The card
   * back is compressed on the x axis until its edge, the already prepared
   * symbol grid is exposed at the midpoint, and the cover then fades away.
   * Keeping this in the renderer preserves the precomputed result while
   * avoiding an abrupt destroy-and-rebuild swap at reel stop.
   */
  private async animateCardReveal(reel: number, durationMs: number, signal: AbortSignal): Promise<void> {
    const cards = this.cardsLayer.children.filter((child) => child.label.startsWith(`card:${reel}:`));
    if (cards.length === 0) return;

    const startedAt = now();
    let symbolsRevealed = false;
    await new Promise<void>((resolve, reject) => {
      let frame = 0;
      let settled = false;
      const cancel = (): void => {
        if (settled) return;
        settled = true;
        if (frame !== 0) globalThis.cancelAnimationFrame(frame);
        signal.removeEventListener('abort', cancel);
        reject(new DOMException('Karten-Reveal abgebrochen.', 'AbortError'));
      };
      const tick = (): void => {
        const progress = Math.min(1, (now() - startedAt) / Math.max(1, durationMs));
        const flipProgress = Math.min(1, progress / 0.52);
        const fadeProgress = Math.max(0, (progress - 0.48) / 0.52);
        const widthScale = progress < 0.52
          ? 1 - easeInOutCubic(flipProgress) * 0.94
          : 0.06;
        const depthScale = 1 + Math.sin(progress * Math.PI) * 0.018;
        const coverAlpha = progress < 0.48 ? 1 : 1 - easeOutCubic(fadeProgress);
        cards.forEach((card) => {
          card.scale.set(widthScale, depthScale);
          card.alpha = coverAlpha;
        });

        if (!symbolsRevealed && progress >= 0.48) {
          symbolsRevealed = true;
          this.reelContents[reel]?.children.forEach((symbol) => { symbol.alpha = 1; });
        }

        if (progress >= 1) {
          settled = true;
          cards.forEach((card) => { card.alpha = 0; });
          signal.removeEventListener('abort', cancel);
          resolve();
          return;
        }
        frame = globalThis.requestAnimationFrame(tick);
      };
      signal.addEventListener('abort', cancel, { once: true });
      frame = globalThis.requestAnimationFrame(tick);
    });
  }

  private async presentStickyReveal(
    result: SpinPresentationResult,
    reducedMotion: boolean,
    signal: AbortSignal,
  ): Promise<void> {
    this.eventSink.emit({ type: 'sticky-reveal', positions: result.newlySticky });
    if (!reducedMotion) {
      for (const position of result.newlySticky) {
        const center = getCellCenter(position);
        const ring = new Graphics()
          .circle(center.x, center.y, 78)
          .stroke({ color: 0xffdf67, width: 8, alpha: 0.95 });
        this.effectsLayer.addChild(ring);
        await wait(85, signal);
        ring.destroy();
      }
    }
    this.setGrid(result.grid, result.stickyAfter);
  }

  private async showcaseWins(
    lines: readonly PresentedLineWin[],
    turbo: boolean,
    reducedMotion: boolean,
    signal?: AbortSignal,
  ): Promise<void> {
    this.clearWinPresentation();
    const plan = createWinShowcasePlan(lines, turbo, reducedMotion);
    for (let index = 0; index < plan.initial.length; index += 1) {
      const step = plan.initial[index]!;
      this.drawSingleWin(step.line);
      this.eventSink.emit({ type: 'line-show', line: step.line, ordinal: index + 1, total: lines.length });
      if (step.durationMs > 0) await wait(step.durationMs, signal);
    }
    if (lines.length === 1) this.drawSingleWin(lines[0]!);
    if (plan.cycleIntervalMs > 0) {
      let index = 0;
      this.idleWinTimer = globalThis.setInterval(() => {
        index = (index + 1) % lines.length;
        this.drawSingleWin(lines[index]!);
      }, plan.cycleIntervalMs);
    }
  }

  private drawSingleWin(line: PresentedLineWin): void {
    this.winLayer.removeChildren().forEach((child) => child.destroy({ children: true }));
    const points = getVisibleLineRoute(line.route, line.count).map((position) => getCellCenter(position));
    if (points.length < 2) return;
    const color = PAYLINE_COLORS[line.lineIndex % PAYLINE_COLORS.length]!;
    const path = new Graphics().moveTo(points[0]!.x, points[0]!.y);
    points.slice(1).forEach((point) => path.lineTo(point.x, point.y));
    path.stroke({ color: 0x080503, width: 10, alpha: 0.72, cap: 'round', join: 'round' });
    const light = new Graphics().moveTo(points[0]!.x, points[0]!.y);
    points.slice(1).forEach((point) => light.lineTo(point.x, point.y));
    light.stroke({ color, width: 4.5, alpha: 0.96, cap: 'round', join: 'round' });
    const markers = new Container();
    points.forEach((point) => {
      markers.addChild(new Graphics()
        .circle(point.x, point.y, 9)
        .fill({ color, alpha: 0.96 })
        .circle(point.x, point.y, 3.5)
        .fill({ color: 0xfff2c5, alpha: 0.95 }));
    });
    this.winLayer.addChild(path, light, markers);
  }

  private async presentPotOfGreed(
    position: GridPosition,
    turbo: boolean,
    reducedMotion: boolean,
    signal?: AbortSignal,
  ): Promise<void> {
    const timeline = createPotOfGreedTimeline(turbo, reducedMotion);
    const root = this.potOverlay ?? this.createPotOverlay(position, true);
    if (root.parent !== this.effectsLayer) {
      root.parent?.removeChild(root);
      this.effectsLayer.addChild(root);
    }
    root.alpha = 1;
    root.scale.set(1);
    this.eventSink.emit({ type: 'pot-highlight', position });
    await wait(timeline.highlightAtMs, signal);
    this.eventSink.emit({ type: 'pot-awarded', position, awarded: 1 });
    this.eventSink.emit({ type: 'pot-flip', position });
    await this.animatePotFlip(root, timeline.transformAtMs - timeline.flipAtMs, signal);
    root.alpha = 0;
    const grid = this.currentGrid;
    const stickyAfter = [...this.stickyPositions, position];
    if (grid !== null) this.setGrid(grid, stickyAfter);
    this.eventSink.emit({ type: 'pot-transformed', position });
    await wait(timeline.completeAtMs - timeline.transformAtMs, signal);
    this.clearPotOverlay();
  }

  private async animatePotFlip(root: Container, durationMs: number, signal?: AbortSignal): Promise<void> {
    const startedAt = now();
    await new Promise<void>((resolve, reject) => {
      let frame = 0;
      let settled = false;
      const cancel = (): void => {
        if (settled) return;
        settled = true;
        if (frame !== 0) globalThis.cancelAnimationFrame(frame);
        signal?.removeEventListener('abort', cancel);
        reject(new DOMException('Topf-Reveal abgebrochen.', 'AbortError'));
      };
      const tick = (): void => {
        const progress = Math.min(1, (now() - startedAt) / Math.max(1, durationMs));
        root.scale.set(1 - easeInOutCubic(progress) * 0.94, 1 + Math.sin(progress * Math.PI) * 0.02);
        if (progress >= 1) {
          settled = true;
          signal?.removeEventListener('abort', cancel);
          resolve();
          return;
        }
        frame = globalThis.requestAnimationFrame(tick);
      };
      signal?.addEventListener('abort', cancel, { once: true });
      frame = globalThis.requestAnimationFrame(tick);
    });
  }

  private clearSpinVisuals(): void {
    this.performanceStrips.forEach((strip) => {
      if (!strip.root.destroyed) strip.root.destroy({ children: true });
    });
    this.performanceStrips = [];
    this.clearCardCovers(false);
    this.clearPotOverlay();
    this.reelGlows.forEach((glow) => { glow.alpha = 0; });
    if (this.initialized) delete this.app.canvas.dataset.scatterAnticipation;
  }

  private clearCardCovers(revealSymbols: boolean): void {
    this.cardsLayer.removeChildren().forEach((child) => child.destroy({ children: true }));
    if (revealSymbols) {
      this.reelContents.forEach((content) => content.children.forEach((symbol) => { symbol.alpha = 1; }));
    }
    this.coveredCellKeys.clear();
    this.syncCardCoverState();
  }

  private preparePotOverlay(position: GridPosition | null, visible: boolean): void {
    if (position === null) return;
    this.potOverlay = this.createPotOverlay(position, visible);
  }

  private createPotOverlay(position: GridPosition, visible: boolean): Container {
    this.clearPotOverlay();
    const center = getCellCenter(position);
    const texture = this.textures.get(POT_OF_GREED_URL) ?? Texture.EMPTY;
    const root = new Container();
    root.label = 'pot-of-greed';
    root.position.set(center.x, center.y);
    // The overlay is a complete opaque symbol tile. The stored grid keeps the
    // evaluated symbol underneath, but no part of that card may remain
    // visible while the Pot of Greed is being highlighted or flipped.
    const card = new Graphics()
      .roundRect(
        -LAYOUT.reelWidth / 2,
        -LAYOUT.symbolHeight / 2,
        LAYOUT.reelWidth,
        LAYOUT.symbolHeight,
        12,
      )
      .fill({ color: 0x041d18, alpha: 1 })
      .stroke({ color: 0x8ad8a8, width: 2.8, alpha: 0.98 })
      .roundRect(
        -LAYOUT.reelWidth / 2 + 5,
        -LAYOUT.symbolHeight / 2 + 5,
        LAYOUT.reelWidth - 10,
        LAYOUT.symbolHeight - 10,
        9,
      )
      .fill({ color: 0x0b4938, alpha: 1 })
      .stroke({ color: 0xd7b85e, width: 1.2, alpha: 0.8 })
      .rect(
        -LAYOUT.reelWidth / 2 + 7,
        -LAYOUT.symbolHeight / 2 + 7,
        LAYOUT.reelWidth - 14,
        53,
      )
      .fill({ color: 0x47b57b, alpha: 0.2 })
      .rect(
        -LAYOUT.reelWidth / 2 + 7,
        32,
        LAYOUT.reelWidth - 14,
        42,
      )
      .fill({ color: 0x02140f, alpha: 0.42 });
    const aura = new Graphics()
      .ellipse(0, -9, 62, 49)
      .fill({ color: 0x7be7a7, alpha: 0.14 })
      .ellipse(0, -9, 48, 37)
      .stroke({ color: 0xd7b85e, width: 1.5, alpha: 0.42 });
    const visual = texture === Texture.EMPTY
      ? new Text({ text: 'TOPF', style: textStyle(27, 0xffd45f), resolution: TEXT_TEXTURE_RESOLUTION })
      : new Sprite(texture);
    if (visual instanceof Sprite) {
      visual.anchor.set(0.5);
      const fitted = fitContain(texture.width, texture.height, LAYOUT.reelWidth - 26, 104);
      visual.width = fitted.width;
      visual.height = fitted.height;
      visual.position.set(0, -9);
    } else {
      visual.anchor.set(0.5);
      visual.position.set(0, -8);
    }
    const label = new Text({ text: 'TOPF DER GIER', style: textStyle(10, 0xffd9a0, '600'), resolution: TEXT_TEXTURE_RESOLUTION });
    label.anchor.set(0.5);
    label.position.set(0, LAYOUT.symbolHeight / 2 - 19);
    root.addChild(card, aura, visual, label);
    root.alpha = visible ? 1 : 0;
    this.potLayer.addChild(root);
    this.potOverlay = root;
    this.potOverlayPosition = position;
    return root;
  }

  private setPotOverlayVisible(visible: boolean): void {
    if (this.potOverlay !== null) this.potOverlay.alpha = visible ? 1 : 0;
  }

  private clearPotOverlay(): void {
    if (this.potOverlay !== null) {
      this.potOverlay.removeFromParent();
      this.potOverlay.destroy({ children: true });
    }
    this.potOverlay = null;
    this.potOverlayPosition = null;
  }

  private syncCardCoverState(): void {
    this.app.canvas.dataset.coveredCells = String(this.coveredCellKeys.size);
    this.app.canvas.dataset.cardState = this.coveredCellKeys.size > 0 ? 'covered' : 'revealed';
  }

  private clearIdleWinCycle(): void {
    if (this.idleWinTimer !== null) globalThis.clearInterval(this.idleWinTimer);
    this.idleWinTimer = null;
  }

  private clearWinPresentation(): void {
    this.clearIdleWinCycle();
    this.winLayer.removeChildren().forEach((child) => child.destroy({ children: true }));
  }
}
